/**
 * NetworkOverlay — Full-screen overlay rendered on top of the Lightning.js canvas.
 *
 * Shows network-loss messages, reconnection progress, and retry/go-back controls
 * with auto-focus management for WebOS remote control navigation.
 */

import { createEffect, Show } from "solid-js";
import type { ConnectionType } from "../hooks/useNetworkMonitor";
import { hexColor, Text, View } from "@lightningtv/solid";
import { isRTL, t } from "../translations/configuration";

export interface NetworkOverlayProps {
  visible: boolean;
  isReconnecting: boolean;
  connectionType: ConnectionType;
  attemptNumber: number;
  maxAttempts: number;
  onRetry: () => void;
  onGoBack?: () => void;
}

// ── Detect WebOS 3.x for requestAnimationFrame focus workaround ──
function isWebOS3(): boolean {
  const match = navigator.userAgent.match(/Chrome\/(\d+)/);
  return match ? parseInt(match[1], 10) < 53 : false;
}

function safeFocus(el: any | undefined): void {
  if (!el) return;
  if (isWebOS3()) {
    requestAnimationFrame(() => el?.setFocus?.());
  } else {
    el?.setFocus?.();
  }
}

const NetworkOverlay = (props: NetworkOverlayProps) => {
  let retryBtnRef: any;
  let goBackBtnRef: any;

  const allRetriesExhausted = () =>
    props.attemptNumber >= props.maxAttempts && !props.isReconnecting;

  const connectionHint = () => {
    switch (props.connectionType) {
      case "wifi":
        return t('check_wifi');
      case "ethernet":
        return t('check_ethernet');
      default:
        return t('check_internet');
    }
  };

  const titleText = () => {
    if (allRetriesExhausted()) return t('unable_to_resume');
    if (props.isReconnecting) return t('reconnecting');
    return t('no_internet_connection');
  };

  const subtitleText = () => {
    if (allRetriesExhausted())
      return t('please_try_again');
    if (props.isReconnecting)
      return t('restore_stream');
    return t('reconnect_automatically');
  };

  // Auto-focus the primary button when overlay becomes visible
  createEffect(() => {
    if (props.visible) {
      setTimeout(() => {
        if (allRetriesExhausted()) {
          safeFocus(retryBtnRef);
        } else if (!props.isReconnecting) {
          safeFocus(retryBtnRef);
        }
      }, 100);
    }
  });

  // Handle keyboard navigation between buttons
  function handleRetryKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.keyCode === 13) {
      e.preventDefault();
      props.onRetry?.();
    } else if (e.key === "ArrowDown" || e.keyCode === 40) {
      if (allRetriesExhausted() && goBackBtnRef) {
        e.preventDefault();
        safeFocus(goBackBtnRef);
      }
    }
  }

  function handleGoBackKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.keyCode === 13) {
      e.preventDefault();
      props.onGoBack?.();
    } else if (e.key === "ArrowUp" || e.keyCode === 38) {
      e.preventDefault();
      safeFocus(retryBtnRef);
    }
  }

  return (
    <Show when={props.visible}>
      {/* Root overlay — semi-transparent dark scrim, centered column */}
      <View
        style={{
          width: 1920,
          height: 1080,
          color: 0x00000099,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 99999,
        }}
      >
        {/* ── Icon / Spinner ── */}
        <View
          style={{
            width: 100,
            height: 100,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 32,
          }}
        >
          <Show
            when={!props.isReconnecting}
            fallback={
              // Spinner ring
              <View
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  border: 6,
                  borderColor: 0x3b82f6ff,
                  color: 0x00000000,
                }}
              />
            }
          >
            {/* Bold red ✕ matching screenshot */}
            <Text
              style={{
                fontSize: 96,
                fontWeight: isRTL() ? "normal" : "bold",
                color: 0xef4444ff,
                lineHeight: 1,
              }}
            >
              X
            </Text>
          </Show>
        </View>

        {/* ── Title — white, heavy ── */}
        <Text
          style={{
            fontSize: 56,
            fontWeight: isRTL() ? "normal" : "bold",
            color: 0xffffffff,
            textAlign: "center",
            marginBottom: 28,
          }}
        >
          {titleText()}
        </Text>

        {/* ── Subtitle — light gray ── */}
        <Text
          style={{
            fontSize: 30,
            color: 0xd1d5dbff,
            textAlign: "center",
            maxWidth: 900,
            marginBottom: 28,
          }}
        >
          {subtitleText()}
        </Text>

        {/* ── "Waiting for connection..." amber text ── */}
        <Show when={!props.isReconnecting && !allRetriesExhausted()}>
          <Text
            style={{
              fontSize: 30,
              color: 0xf59e0bff,
              textAlign: "center",
              marginBottom: 16,
            }}
          >
            {t("waiting_connection")}
          </Text>
        </Show>

        {/* ── Connection hint — small muted gray below amber ── */}
        <Show when={!props.isReconnecting && !allRetriesExhausted()}>
          <Text
            style={{
              fontSize: 26,
              color: 0x9ca3afff,
              textAlign: "center",
              marginBottom: 52,
            }}
          >
            {connectionHint()}
          </Text>
        </Show>

        {/* ── Attempt counter (reconnecting state, attempt > 1) ── */}
        <Show when={props.isReconnecting && props.attemptNumber > 1}>
          <Text
            style={{
              fontSize: 26,
              color: 0x9ca3afff,
              textAlign: "center",
              marginBottom: 52,
            }}
          >
            {t('reconnection_attempt')}{` ${props.attemptNumber} of ${props.maxAttempts}`}
          </Text>
        </Show>

        {/* Spacer when reconnecting with no counter shown */}
        <Show when={props.isReconnecting && props.attemptNumber <= 1}>
          <View style={{ height: 52 }} />
        </Show>

        {/* ── Buttons ── */}
        <Show when={!props.isReconnecting}>
          <View
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 20,
            }}
          >
            {/* Try Again — white fill, blue border, dark label (matches screenshot) */}
            <View
              ref={retryBtnRef}
              onKeyDown={handleRetryKeyDown}
              onEnter={() => props.onRetry?.()}
              autofocus
              style={{
                width: 340,
                height: 90,
                borderRadius: 18,
                color: hexColor("#ffffff"),
                border: 4,
                borderColor: hexColor("#2563eb"),
                alignItems: "center",
                justifyContent: "center",
                focus: { borderColor: hexColor("#e7e7e7") },
                transition: { color: { duration: 150 } },
              }}
              focus={{
                borderColor: hexColor("#2563eb"),   // blue border appears on focus
                color: hexColor("#2563eb"),         // slightly tinted white on focus
              }}
            >
              <Text
                x={100}
                y={30}
                style={{
                  fontSize: 32,
                  fontWeight: isRTL() ? "normal" : "bold",
                  color: 0x1e293bff,
                }}
              >
                {t('try_again')}
              </Text>
            </View>

            {/* Go Back — dark fill, only when retries exhausted */}
            <Show when={allRetriesExhausted() && props.onGoBack}>
              <View
                ref={goBackBtnRef}
                onKeyDown={handleGoBackKeyDown}
                onClick={() => props.onGoBack?.()}
                focusable
                style={{
                  width: 340,
                  height: 90,
                  borderRadius: 18,
                  color: 0x374151ff,
                  border: 4,
                  borderColor: 0x4b5563ff,
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onFocus={(e: any) => {
                  e.currentTarget.style.borderColor = 0x9ca3afff;
                  e.currentTarget.style.color = 0x4b5563ff;
                }}
                onBlur={(e: any) => {
                  e.currentTarget.style.borderColor = 0x4b5563ff;
                  e.currentTarget.style.color = 0x374151ff;
                }}
              >
                <Text
                  x={100}
                  y={30}
                  style={{
                    fontSize: 32,
                    fontWeight: isRTL() ? "normal" : "bold",
                    color: 0xd1d5dbff,
                  }}
                >
                  {t('go_back')}
                </Text>
              </View>
            </Show>
          </View>
        </Show>
      </View>
    </Show>
  );
};

export default NetworkOverlay;